*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

:root {
  --blue: #0d6efd;
  --blue-hover: #0b5ed7;
  --blue-light: #cfe2ff;
  --blue-text: #084298;
  --green: #198754;
  --green-hover: #157347;
  --green-light: #d1e7dd;
  --green-bg: #f0fbf4;
  --red: #dc3545;
  --red-hover: #bb2d3b;
  --red-light: #f8d7da;
  --yellow-light: #fff3cd;
  --yellow-text: #664d03;
  --gray: #6c757d;
  --gray-light: #f8f9fa;
  --gray-border: #dee2e6;
  --text: #212529;
  --bg: #f8f9fa;
  --card-bg: #fff;
  --shadow: 0 1px 6px rgba(0, 0, 0, 0.1);
  --radius: 8px;
  --radius-lg: 12px;
}

html,
body,
#root {
  height: 100%;
}

body {
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 0.9375rem;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}

/* ── Buttons ─────────────────────────────────────────────── */

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border: 1.5px solid transparent;
  border-radius: var(--radius);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    opacity 0.15s;
  white-space: nowrap;
  background: transparent;
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--blue);
  color: #fff;
  border-color: var(--blue);
}

.btn-primary:hover:not(:disabled) {
  background: var(--blue-hover);
  border-color: var(--blue-hover);
}

.btn-outline {
  background: transparent;
  border-color: var(--gray-border);
  color: var(--text);
}

.btn-outline:hover:not(:disabled) {
  background: var(--gray-light);
}

.btn-danger {
  background: transparent;
  color: var(--red);
  border-color: var(--red);
}

.btn-danger:hover:not(:disabled) {
  background: var(--red-light);
}

.btn-success {
  background: var(--green);
  color: #fff;
  border-color: var(--green);
}

.btn-success:hover:not(:disabled) {
  background: var(--green-hover);
  border-color: var(--green-hover);
}

.btn-sm {
  padding: 0.3rem 0.625rem;
  font-size: 0.8125rem;
}

.btn-full {
  width: 100%;
}

/* ── Form fields ─────────────────────────────────────────── */

.field-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 0.375rem;
}

.field-input {
  display: block;
  width: 100%;
  padding: 0.5625rem 0.75rem;
  border: 1.5px solid var(--gray-border);
  border-radius: var(--radius);
  font-size: 0.9375rem;
  color: var(--text);
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
  margin-bottom: 1rem;
}

.field-input:focus {
  border-color: var(--blue);
}

/* ── Login page ──────────────────────────────────────────── */

.login-gate {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 2rem 1rem;
  background: var(--bg);
}

.login-card {
  background: #fff;
  border: 1px solid var(--gray-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  padding: 2.5rem 2rem;
  width: 100%;
  max-width: 22rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.brand-logo {
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  object-fit: contain;
}

.brand-name {
  font-weight: 700;
  font-size: 1rem;
  color: var(--text);
}

.login-card h1 {
  font-size: 1.375rem;
  font-weight: 700;
}

.login-subtitle {
  font-size: 0.9rem;
  color: var(--gray);
  margin-top: -0.75rem;
}

/* ── Staff page layout ───────────────────────────────────── */

.staff-page {
  min-height: 100%;
  padding: 1.5rem 1rem;
}

.staff-container {
  max-width: 1200px;
  margin: 0 auto;
}

.staff-card {
  background: var(--card-bg);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* ── Card header ─────────────────────────────────────────── */

.staff-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.staff-header h1 {
  font-size: 1.5rem;
  font-weight: 700;
}

.staff-header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.header-email {
  font-size: 0.8125rem;
  color: var(--gray);
}

/* ── Provider dashboard box ──────────────────────────────── */

.provider-dashboard {
  border: 2px solid var(--green);
  background: var(--green-bg);
  border-radius: var(--radius-lg);
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition:
    border-color 0.2s,
    background 0.2s;
}

.provider-dashboard.idle {
  border-color: var(--gray-border);
  background: var(--gray-light);
}

.provider-dashboard-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.provider-status-label {
  font-size: 0.8125rem;
  color: var(--gray);
  margin-bottom: 0.125rem;
}

.provider-status-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.125rem;
}

.provider-status-text {
  font-size: 0.875rem;
  color: var(--gray);
}

.provider-timer-wrap {
  text-align: right;
  flex-shrink: 0;
}

.provider-timer {
  font-size: 1.75rem;
  font-weight: 700;
}

/* ── Action buttons grid ─────────────────────────────────── */

.action-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

@media (max-width: 560px) {
  .action-grid {
    grid-template-columns: 1fr;
  }
}

/* ── Queue sections ──────────────────────────────────────── */

.queue-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.queue-section h5 {
  font-size: 1rem;
  font-weight: 700;
}

.queue-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.queue-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  border: 1px solid var(--gray-border);
  border-radius: var(--radius);
  color: var(--gray);
  font-size: 0.875rem;
}

/* ── Entry card ──────────────────────────────────────────── */

.entry-card {
  background: #fff;
  border: 1px solid var(--gray-border);
  border-radius: var(--radius);
  padding: 0.875rem 1.125rem;
}

.entry-card.status-in_progress {
  border-left: 3px solid var(--blue);
}

.entry-row {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.entry-position {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--gray);
  min-width: 1.5rem;
  text-align: center;
  padding-top: 0.125rem;
  flex-shrink: 0;
}

.entry-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.entry-main-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.entry-left {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.entry-times {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.375rem;
  text-align: right;
  flex-shrink: 0;
}

.entry-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.entry-status {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.status-badge {
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.status-badge.waiting {
  background: var(--yellow-light);
  color: var(--yellow-text);
}

.status-badge.in_progress {
  background: var(--blue-light);
  color: var(--blue-text);
}

.status-badge.completed {
  background: var(--blue-light);
  color: var(--blue-text);
}

.status-badge.cancelled {
  background: var(--gray-light);
  color: var(--gray);
}

.entry-time {
  font-size: 0.8125rem;
  color: var(--gray);
}

.entry-id {
  font-size: 0.75rem;
  color: var(--gray);
  font-family: monospace;
}

.entry-name-row {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.entry-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
}

.copy-btn {
  display: inline-grid;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1;
  padding: 0.25rem 0.5rem;
  color: var(--gray);
  background: transparent;
  border: 1px solid var(--gray-border);
  border-radius: 4px;
  cursor: pointer;
}

.copy-btn:hover,
.copy-btn:focus-visible {
  color: var(--text);
  background: var(--gray-light);
}

/* Both labels share one grid cell so the button keeps its width while swapping. */
.copy-btn-label {
  grid-area: 1 / 1;
}

.copy-btn-done,
.copy-btn.copied .copy-btn-label:first-child {
  visibility: hidden;
}

.copy-btn.copied .copy-btn-done {
  visibility: visible;
}

.copy-btn.copied {
  color: var(--green);
  border-color: var(--green);
  cursor: default;
}

.entry-patient {
  font-size: 0.875rem;
  color: var(--gray);
}

.entry-meta {
  font-size: 0.8125rem;
  color: var(--gray);
}

.entry-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.cancel-group {
  display: flex;
  gap: 0.375rem;
}

.entry-error {
  font-size: 0.8125rem;
  color: var(--red);
}

/* ── Completed table ─────────────────────────────────────── */

.completed-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.completed-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.completed-table th,
.completed-table td {
  padding: 0.5rem 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--gray-border);
  white-space: nowrap;
}

.completed-table th {
  font-weight: 600;
  font-size: 0.8125rem;
  color: var(--gray);
}

.completed-table tbody tr:nth-child(odd) {
  background: var(--gray-light);
}

.completed-table td.muted {
  color: var(--gray);
  font-size: 0.8125rem;
}

.completed-table .completed-table-actions {
  width: 1%;
  padding-left: 0;
  text-align: right;
}

.kebab-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: var(--radius);
  background: transparent;
  color: var(--gray);
  cursor: pointer;
}

.kebab-btn:hover {
  background: var(--gray-light);
  color: var(--text);
}

.submission-details-title {
  text-align: center;
}

.submission-details {
  display: grid;
  grid-template-columns: auto auto;
  justify-content: start;
  gap: 0.5rem 1.5rem;
  font-size: 0.875rem;
}

.submission-details dt {
  font-weight: 600;
  text-align: left;
}

.submission-details dd {
  color: var(--gray);
  text-align: left;
}

/* ── Submission checks ───────────────────────────────────── */

.submission-checks {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.8125rem;
}

.submission-check {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.submission-check-mark {
  color: var(--green);
  font-weight: 700;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ── Shift error ─────────────────────────────────────────── */

.shift-error {
  font-size: 0.8125rem;
  color: var(--red);
}

/* ── Last updated ─────────────────────────────────────────── */

.last-updated {
  font-size: 0.8125rem;
  color: var(--gray);
  background: var(--gray-light);
  border: 1px solid var(--gray-border);
  border-radius: var(--radius);
  padding: 0.625rem 0.875rem;
}

/* ── Spinner ─────────────────────────────────────────────── */

.spinner {
  width: 2rem;
  height: 2rem;
  border: 2.5px solid var(--blue-light);
  border-top-color: var(--blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Responsive ──────────────────────────────────────────── */

@media (max-width: 600px) {
  .staff-page {
    padding: 1rem 0.75rem;
  }

  .staff-card {
    padding: 1.125rem;
  }

  .provider-dashboard {
    padding: 1rem;
  }

  .provider-timer-wrap {
    text-align: left;
  }
}

/* ── Toast ───────────────────────────────────────────────── */

.toast-stack {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 22rem;
  width: calc(100% - 2rem);
}

.toast-item {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  background: #fff;
  border: 1px solid var(--red);
  border-left: 4px solid var(--red);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: 0.75rem 0.875rem;
  animation: toast-in 0.15s ease;
}

.toast-item--success {
  border-color: var(--green);
  border-left-color: var(--green);
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateX(0.5rem);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.toast-message {
  flex: 1;
  font-size: 0.875rem;
  color: var(--text);
  line-height: 1.4;
}

.toast-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.125rem;
  line-height: 1;
  color: var(--gray);
  padding: 0;
  flex-shrink: 0;
}

.toast-close:hover {
  color: var(--text);
}

/* ── Modal ───────────────────────────────────────────────── */

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 1rem;
}

.modal {
  background: var(--card-bg);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  padding: 1.5rem;
  width: 100%;
  max-width: 24rem;
}

.modal-title {
  font-size: 1.0625rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.modal > * + * {
  margin-top: 0.75rem;
}

.modal-textarea {
  resize: vertical;
  font-family: inherit;
}

.modal-note-count {
  margin-top: -0.75rem;
  margin-bottom: 1rem;
  font-size: 0.75rem;
  color: var(--gray);
  text-align: right;
}

.modal-note-count-error {
  color: var(--red);
  font-weight: 600;
}

.modal-error {
  margin-top: -0.75rem;
  margin-bottom: 1rem;
  font-size: 0.8125rem;
  color: var(--red);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
}

.modal-divider {
  border: none;
  border-top: 1px solid var(--gray-border);
}

/* ── Invite link ──────────────────────────────────────────── */

.invite-link-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.invite-link-row .field-input {
  margin-bottom: 0;
}

.invite-link-guidance {
  font-size: 0.8125rem;
  color: var(--gray);
  margin-bottom: 1rem;
}

.invite-email-description {
  font-size: 0.8125rem;
  color: var(--gray);
}

/* ── Cancellation note marker ────────────────────────────── */

.note-marker {
  cursor: help;
  color: var(--gray);
  font-weight: 700;
  margin-left: 0.125rem;
}

/* ── Queue status hover marker ───────────────────────────── */

.queue-status-hover {
  cursor: help;
  text-decoration: underline dotted;
}

/* ── Nav menu ─────────────────────────────────────────────── */

.nav-menu-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  border-radius: var(--radius);
  font-size: 1.125rem;
  line-height: 1;
  color: var(--text);
  cursor: pointer;
}

.nav-menu-toggle:hover {
  background: var(--gray-light);
}

.nav-menu-panel {
  flex-shrink: 0;
  width: max-content;
  min-width: 10rem;
  background: #fff;
  border-right: 1px solid var(--gray-border);
  box-shadow: var(--shadow);
  padding: 0.75rem 0.5rem;
}

.nav-menu-title {
  padding: 0.375rem 0.625rem 0.625rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.nav-menu-item {
  display: block;
  padding: 0.5rem 0.625rem;
  font-size: 0.875rem;
  color: var(--text);
  border-radius: var(--radius);
  cursor: pointer;
}

.nav-menu-item:hover {
  background: var(--gray-light);
}

/* ── Dashboard layout ────────────────────────────────────── */

.dashboard {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.app-shell {
  display: flex;
  flex: 1;
  min-height: 100%;
}

.app-shell-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.app-header {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 1.25rem;
  height: 3.25rem;
  background: #fff;
  border-bottom: 1px solid var(--gray-border);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-title {
  font-weight: 700;
  font-size: 1rem;
  color: var(--text);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.dashboard-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem 1.25rem;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}

/* ── Queue section header ────────────────────────────────── */

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.section-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
}

.queue-count {
  font-size: 0.8125rem;
  color: var(--gray);
  background: var(--gray-light);
  border: 1px solid var(--gray-border);
  border-radius: 9999px;
  padding: 0.15rem 0.625rem;
  font-weight: 600;
}

/* ── Shift panel ─────────────────────────────────────────── */

.shift-panel {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid var(--gray-border);
  border-radius: var(--radius-lg);
  padding: 1rem 1.25rem;
  box-shadow: var(--shadow);
}

.shift-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.status-dot {
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--gray);
}

.status-dot.active {
  background: var(--green);
}

.status-dot.closed {
  background: var(--gray);
}

.status-label {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text);
}

.shift-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

@media (max-width: 600px) {
  .app-header {
    padding: 0 1rem;
  }

  .dashboard-main {
    padding: 1rem;
  }

  .shift-panel {
    flex-direction: column;
    align-items: flex-start;
  }
}
